import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router';
import useSWR from 'swr';
import { Event } from '../../../../../../../typings/Event.interface';
import { EventAttempt } from '../../../../../../../typings/EventAttempt.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { getErrorCodeLabels } from '../../../../utils/error-codes';
import Alert from '../../../common/base/Alert';
import Button from '../../../common/base/Button';
import Divider from '../../../common/base/Divider';
import Link from '../../../common/base/Link';
import Loading from '../../../common/base/Loading';
import Skeleton from '../../../common/base/Skeleton';
import Tabs from '../../../common/base/Tabs';
import Text from '../../../common/base/Text';
import { BodyCollapsibleSection } from '../../../common/Data/Body';
import DropdownMenu from '../../../common/DropdownMenu';
import { Div } from '../../../common/helpers/StyledUtils';
import { Status } from '../../../common/Status';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useCopyAsCurl from '../../../hooks/useCopyAsCurl';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { StyledViewNavHeader, StyledViewNavSecondary, StyledViewSidebar, StyledViewSidebarActions, StyledViewSidebarFooter, StyledViewSidebarNav, StyledViewSidebarScrollableSection, ViewSidebarTitle } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../DashboardContext';
import { EventHTTPRequest } from '../Event/EventHTTPRequest';
import { EventMetadata } from '../Event/EventOverview';
const EventSidebar: React.FC<{
  event_id: string;
  attempt_id?: string;
  list_event?: Event;
  list_attempt?: EventAttempt;
  event_actions?: { [k in 'retry' | 'cancel' | 'bookmark' | 'replay']: (event: Event) => Promise<void> };
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  onSelectedAttempt: (attempt_id: string) => void;
}> = ({
  event_id,
  attempt_id,
  onClose,
  onPrev,
  onNext,
  event_actions,
  list_attempt,
  list_event,
  onSelectedAttempt
}) => {
  const location = useLocation();
  const [tab_preferences, setTabPreference] = useLocalStorage<{
    event: 'request' | 'response' | 'metadata';
    attempt: 'request' | 'response' | 'metadata';
  }>('pref:event-sidebar:active-tab', {
    event: 'request',
    attempt: 'response'
  });
  const active_tab = tab_preferences[attempt_id ? 'attempt' : 'event'] || 'request';
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription,
    organization
  } = useContext(DashboardContext);
  const {
    data: fetched_event
  } = useSWR(APIMethodKeys.events.get(event_id), () => HookdeckAPI.events.get(event_id));
  const event = fetched_event && (list_event ? {
    ...fetched_event,
    ...list_event,
    data: fetched_event.data
  } : fetched_event);
  const {
    data: bookmarks,
    mutate: mutateBookmarks
  } = useSWR(event && APIMethodKeys.bookmarks.list({
    event_data_id: event.event_data_id
  }), () => HookdeckAPI.bookmarks.list({
    event_data_id: event?.event_data_id
  }));
  const {
    data: attempts,
    mutate: mutateAttempts
  } = useSWR(!attempt_id && event_id && APIMethodKeys.attempts.list({
    event_id,
    limit: 1
  }), () => HookdeckAPI.attempts.list({
    event_id,
    limit: 1
  }));
  const resolved_attempt_id = attempt_id || (event?.status === 'FAILED' || event?.status === 'SUCCESSFUL' ? attempts?.models[0]?.id : null);
  const {
    data: attempt,
    mutate
  } = useSWR(resolved_attempt_id && APIMethodKeys.attempts.get(resolved_attempt_id), () => HookdeckAPI.attempts.get(resolved_attempt_id!));
  useEffect(() => {
    if (list_attempt && attempt && list_attempt.id === attempt.id) {
      mutate();
    }
    if (!attempt_id && list_attempt) {
      mutateAttempts();
    }
  }, [list_attempt]);
  useEffect(() => {
    if (attempt) {
      let interval;
      if (!attempt.error_code && !attempt.response_status || attempt.body === 'NOT_AVAILABLE_YET') {
        interval = setInterval(mutate, 3000);
      } else if (interval) {
        clearInterval(interval);
      }
      return () => interval && clearInterval(interval);
    }
  }, [attempt, mutate]);
  useEffect(() => {
    const handle_keydown = (event_0: KeyboardEvent) => {
      if (event_0.key === 'ArrowUp' && onPrev) {
        event_0.preventDefault();
        onPrev();
      } else if (event_0.key === 'ArrowDown' && onNext) {
        event_0.preventDefault();
        onNext();
      } else if (event_0.key === 'Escape') {
        event_0.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handle_keydown);
    return () => {
      window.removeEventListener('keydown', handle_keydown);
    };
  }, [onPrev, onNext, onClose]);
  const {
    error: webhook_error,
    data: webhook,
    isLoading: webhook_loading
  } = useSWR(event && APIMethodKeys.webhooks.get(event.webhook_id), () => HookdeckAPI.webhooks.get(event?.webhook_id as string), {
    shouldRetryOnError: error => error.status !== 410
  });
  const webhook_deleted = webhook_error?.status === 410;
  const copyAsCurl = useCopyAsCurl({
    event: event!
  });
  const tabs = [{
    key: 'request',
    label: 'Request'
  }, {
    key: 'response',
    label: 'Response'
  }];
  if (!attempt_id) {
    tabs.push({
      key: 'metadata',
      label: 'Metadata'
    });
  }
  const bookmarked = bookmarks && bookmarks?.count > 0;
  const error_code = attempt_id ? attempt?.error_code : event?.error_code;
  const nav_header = <StyledViewNavHeader>
      <Div flex={{
      align: 'center',
      gap: 2
    }}>
        <ViewSidebarTitle title={attempt_id || event_id} />
        {attempt_id && attempt && <Status {...attempt} />}
        {!attempt_id && event && <Status {...event} />}
      </Div>
      <StyledViewSidebarActions>
        {event_actions && <>
            <DropdownMenu.Permission role="member" ghost portal icon="horizontal_more" placement="bottom-end" options={[{
          label: 'Retry',
          icon: 'retry',
          disabled: !event || webhook_deleted || event.status === 'QUEUED',
          tooltip: 'Retry delivery of this event',
          onClick: () => event && event_actions.retry(event)
        }, {
          label: 'Replay',
          icon: 'replay',
          disabled: !event || !event.data,
          tooltip: 'Create new events from the original request',
          onClick: () => event && event_actions.replay(event)
        }, {
          label: 'Cancel',
          icon: 'cancel',
          disabled: !event || !['HOLD', 'SCHEDULED', 'QUEUED'].includes(event.status),
          onClick: () => event && event_actions.cancel(event)
        }, {
          label: 'Copy as cURL',
          icon: 'copy',
          tooltip: 'Copy event as cURL',
          disabled: !event,
          onClick: copyAsCurl
        }, {
          label: 'Bookmark',
          icon: bookmarked ? 'bookmark' : 'bookmark_add',
          disabled: bookmarked || !event,
          tooltip: bookmarked ? 'Already Bookmarked' : 'Bookmark this event',
          onClick: () => event && event_actions.bookmark(event).then(() => {
            mutateBookmarks();
          })
        }]} />
            <Divider vertical m={{
          y: 1,
          x: 1.5
        }} />
          </>}
        {!location.pathname.includes(`/events/${event_id}`) && <Button ghost icon="open" to={`/events/${event_id}`} />}
        <Button ghost icon="close" onClick={onClose} />
      </StyledViewSidebarActions>
    </StyledViewNavHeader>;
  if (!event || webhook_loading) {
    return <StyledViewSidebar>
        <StyledViewSidebarNav>{nav_header}</StyledViewSidebarNav>
        <Div flex={{
        grow: true,
        align: 'center',
        justify: 'center'
      }}>
          <Loading />
        </Div>
      </StyledViewSidebar>;
  }
  return <StyledViewSidebar>
      <StyledViewSidebarNav>
        {nav_header}
        <StyledViewNavSecondary>
          <Div>
            {error_code && <Alert neutral inline title={error_code} m={{
            b: 4
          }}>
                {getErrorCodeLabels()[error_code]}
              </Alert>}
            <Tabs active_tab={active_tab} border={false} onTabSelected={(tab: 'request' | 'response') => setTabPreference({
            ...tab_preferences,
            [attempt_id ? 'attempt' : 'event']: tab
          })} tabs={tabs} />
          </Div>
        </StyledViewNavSecondary>
      </StyledViewSidebarNav>
      <StyledViewSidebarScrollableSection>
        {active_tab === 'request' && <>
            {!event!.data ? <Div p={{
          x: 6,
          y: 4
        }}>
                <Text muted as="p">
                  This event is past your archival period of {subscription!.retention_days} days,
                  the request data is no longer available.
                </Text>
                {!organization && <Link to="/settings/organization/plans?highlight=retention_days" icon="upgrade">
                    Upgrade Plan
                  </Link>}
              </Div> : <EventHTTPRequest event={event!} embedded context_menu />}
          </>}
        {active_tab === 'response' && (event?.status === 'FAILED' || event?.status === 'SUCCESSFUL' ? <Div>
              {!attempt || attempt.body === 'NOT_AVAILABLE_YET' ? <Skeleton h={{
          px: 200
        }} w={100} loading /> : <BodyCollapsibleSection body={attempt.body || ''} embedded collapsible={false} type="attempt" id={attempt.id} />}
            </Div> : <Div p={{
        x: 6,
        y: 4
      }}>
              <Text muted as="p">
                Response will be shown once a delivery attempt has been made to your destination.
              </Text>
            </Div>)}
        {active_tab === 'metadata' && <Div p={{
        x: 6,
        b: 8
      }}>
            {event && webhook && <EventMetadata embedded event={event} webhook={webhook} attempts={attempts!} onAttemptSelected={onSelectedAttempt} />}
          </Div>}
      </StyledViewSidebarScrollableSection>
      {(onPrev || onNext) && <StyledViewSidebarFooter>
          <Div w={100} flex={{
        align: 'center',
        gap: 2,
        justify: 'flex-end'
      }}>
            <Button outline icon="chevron_up" onClick={onPrev} />
            <Button outline icon="chevron_down" onClick={onNext} />
          </Div>
        </StyledViewSidebarFooter>}
    </StyledViewSidebar>;
};
export default EventSidebar;